import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"
import Radar from "radar-sdk-js"

export default class extends Controller {
  static targets = [
    "input",
    "results",
    "selectForm",
    "backPath",
    "city",
    "state",
    "zip",
    "country",
    "lat",
    "lng",
    "accuracy",
    "source",
    "currentLocationButton",
    "clearButton",
    "loading",
    "error",
    "errorText"
  ]
  static values = {
    radarPublishableKey: String,
    nearLat: Number,
    nearLng: Number,
    autocompleteUrl: String,
    backPath: String,
    overlay: Boolean
  }

  connect() {
    if (this.radarPublishableKeyValue) {
      Radar.initialize(this.radarPublishableKeyValue)
    }
    this.debouncedSearch = debounce(this.performSearch.bind(this), 500)
    this.searchSequence = 0
    this.updateRightAction()
  }

  disconnect() {
    this.cancelSearch()
  }

  search() {
    this.updateRightAction()
    this.debouncedSearch()
  }

  clear(event) {
    event.preventDefault()
    this.cancelSearch()
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.updateRightAction()
  }

  dismissError() {
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = true
    }
  }

  async useCurrentLocation(event) {
    event.preventDefault()
    this.dismissError()
    this.setLoading(true)
    if (window.Turbo) {
      Turbo.cache.clear()
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          if (this.radarPublishableKeyValue) {
            const result = await Radar.reverseGeocode({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            })
            if (result?.addresses?.length > 0) {
              const { city, stateCode, postalCode, countryCode } = result.addresses[0]
              this.submitLocation({
                city,
                state: stateCode,
                zip: postalCode,
                country: countryCode,
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                source: "geolocation"
              })
              return
            }
          }

          this.submitLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            source: "geolocation"
          })
        } catch (e) {
          console.error("Error reverse geocoding current location", e)
          this.showError("Could not determine your location. Please try searching for a city instead.")
          this.setLoading(false)
        }
      },
      (error) => {
        console.error(error)
        this.showError("Unable to access your location. Please allow location access or search for a city instead.")
        this.setLoading(false)
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 1000 * 60 * 60
      }
    )
  }

  selectResult(event) {
    event.preventDefault()
    const { city, state, zip, country, lat, lng, source } = event.currentTarget.dataset
    this.submitLocation({ city, state, zip, country, lat, lng, source: source || "search" })
  }

  // Submitting the search form reloads the page with server-side results, which is the fallback
  // when JavaScript is unavailable. With JavaScript, search in place instead.
  submitSearch(event) {
    event.preventDefault()
    this.debouncedSearch()
    this.debouncedSearch.flush()
  }

  cancelSearch() {
    this.debouncedSearch?.cancel()
    // Invalidate any in-flight search so its results are discarded
    this.searchSequence += 1
  }

  async performSearch() {
    const sequence = (this.searchSequence += 1)
    const query = this.inputTarget.value.trim()
    if (!query) {
      this.resultsTarget.innerHTML = ""
      return
    }

    if (this.radarPublishableKeyValue) {
      await this.radarAutocomplete(query, sequence)
    } else {
      await this.serverAutocomplete(query, sequence)
    }
  }

  showResults(sequence, render) {
    if (sequence !== this.searchSequence) return
    this.resultsTarget.innerHTML = ""
    render()
  }

  // In the community finder overlay the filter changes without a page load, but the URL is kept
  // in sync, so use the current URL rather than the back path the page was rendered with.
  currentBackPath() {
    if (this.overlayValue) {
      return `${window.location.pathname}${window.location.search}`
    }
    return this.backPathValue
  }

  async radarAutocomplete(query, sequence) {
    try {
      const result = await Radar.autocomplete({
        query,
        near: {
          latitude: this.nearLatValue,
          longitude: this.nearLngValue
        },
        layers: ["locality"],
        countryCode: "US,CA"
      })

      if (result?.addresses?.length > 0) {
        this.showResults(sequence, () => this.renderRadarResults(result.addresses))
        return
      }
    } catch (e) {
      console.error("Radar autocomplete error", e)
    }

    await this.serverAutocomplete(query, sequence)
  }

  async serverAutocomplete(query, sequence) {
    const url = new URL(this.autocompleteUrlValue, window.location.origin)
    url.searchParams.set("q", query)
    const backPath = this.currentBackPath()
    if (backPath) {
      url.searchParams.set("back_path", backPath)
    }

    try {
      const response = await fetch(url.toString(), {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      const html = response.ok ? await response.text() : ""
      this.showResults(sequence, () => {
        this.resultsTarget.innerHTML = html
      })
    } catch (e) {
      console.error("City autocomplete error", e)
      this.showResults(sequence, () => {})
    }
  }

  renderRadarResults(addresses) {
    const list = document.createElement("ul")
    list.className = "city-search-results"

    addresses.forEach((address) => {
      const { latitude, longitude, city, stateCode, postalCode, countryCode } = address
      if (!city || !stateCode) return

      const button = document.createElement("button")
      button.type = "button"
      button.dataset.action = "city-search#selectResult"
      Object.assign(button.dataset, {
        city,
        state: stateCode,
        zip: postalCode || "",
        country: countryCode || "",
        lat: latitude,
        lng: longitude,
        source: "search"
      })

      const name = document.createElement("div")
      name.textContent = `${city}, ${stateCode}`
      const icon = document.createElement("i")
      icon.className = "fa-light fa-angle-right"
      button.append(name, icon)

      const item = document.createElement("li")
      item.className = "city-search-result"
      item.append(button)
      list.append(item)
    })

    if (list.children.length > 0) {
      this.resultsTarget.append(list)
    }
  }

  submitLocation(location) {
    this.backPathTarget.value = this.currentBackPath() || ""
    this.cityTarget.value = location.city || ""
    this.stateTarget.value = location.state || ""
    this.zipTarget.value = location.zip || ""
    this.countryTarget.value = location.country || ""
    this.latTarget.value = location.lat
    this.lngTarget.value = location.lng
    this.accuracyTarget.value = location.accuracy || ""
    this.sourceTarget.value = location.source || "search"
    this.selectFormTarget.submit()
  }

  updateRightAction() {
    const hasQuery = this.inputTarget.value.trim().length > 0
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.hidden = !hasQuery
    }
    if (this.hasCurrentLocationButtonTarget) {
      this.currentLocationButtonTarget.hidden = hasQuery
    }
  }

  setLoading(loading) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.hidden = !loading
    }
    if (loading) {
      if (this.hasCurrentLocationButtonTarget) {
        this.currentLocationButtonTarget.hidden = true
      }
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.hidden = true
      }
    } else {
      this.updateRightAction()
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = false
      if (this.hasErrorTextTarget) {
        this.errorTextTarget.textContent = message
      }
    }
  }
}
